import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../../../util/reactIntl';
import * as validators from '../../../../util/validators';
import { Form, PrimaryButton, FieldTextInput } from '../../../../components';

import css from '../SignupWizard.module.css';

const EmailStepComponent = props => {
  const {
    formData,
    onSubmit,
    intl,
    title,
    inviteContent,
    socialLoginButtons,
    loginLink,
    emailLocked,
    lockedEmail,
  } = props;

  return (
    <FinalForm
      initialValues={{ email: formData.email }}
      onSubmit={values => {
        // Server is the authoritative gate, but make sure UI doesn't
        // forward a tampered email when the invite locked it down.
        const finalValues = emailLocked ? { ...values, email: lockedEmail } : values;
        return onSubmit(finalValues);
      }}
      render={fieldRenderProps => {
        const { handleSubmit, invalid, submitting, values } = fieldRenderProps;

        const emailLabel = intl.formatMessage({ id: 'EmailSignupForm.emailLabel' });
        const emailPlaceholder = intl.formatMessage({ id: 'EmailSignupForm.emailPlaceholder' });
        const emailRequiredMessage = intl.formatMessage({ id: 'EmailSignupForm.emailRequired' });
        const emailInvalidMessage = intl.formatMessage({ id: 'EmailSignupForm.emailInvalid' });

        const emailRequired = validators.required(emailRequiredMessage);
        const emailValid = validators.emailFormatValid(emailInvalidMessage);

        // When invite-locked, require the value to match the invited email.
        const inviteMismatchMessage = intl.formatMessage({
          id: 'EmailSignupForm.inviteEmailMismatch',
        }, { email: lockedEmail });
        const matchesInvitedEmail = () => value =>
          !emailLocked || (value && value.toLowerCase() === String(lockedEmail).toLowerCase())
            ? undefined
            : inviteMismatchMessage;

        const submitDisabled =
          invalid ||
          submitting ||
          (emailLocked && values?.email?.toLowerCase() !== String(lockedEmail).toLowerCase());

        return (
          <Form className={css.stepForm} onSubmit={handleSubmit}>
            {title && <h1 className={css.emailStepTitle}>{title}</h1>}
            {inviteContent}

            <FieldTextInput
              className={css.field}
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              readOnly={emailLocked}
              validate={validators.composeValidators(
                emailRequired,
                emailValid,
                matchesInvitedEmail()
              )}
            />

            <div className={css.bottomWrapper}>
              <PrimaryButton type="submit" disabled={submitDisabled}>
                <FormattedMessage id="SignupWizard.nextButton" />
              </PrimaryButton>
            </div>

            {socialLoginButtons}
            {loginLink}
          </Form>
        );
      }}
    />
  );
};

EmailStepComponent.defaultProps = {
  title: null,
  inviteContent: null,
  socialLoginButtons: null,
  loginLink: null,
  emailLocked: false,
  lockedEmail: '',
};

EmailStepComponent.propTypes = {
  formData: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  title: PropTypes.node,
  inviteContent: PropTypes.node,
  socialLoginButtons: PropTypes.node,
  loginLink: PropTypes.node,
  emailLocked: PropTypes.bool,
  lockedEmail: PropTypes.string,
};

const EmailStep = compose(injectIntl)(EmailStepComponent);

export default EmailStep;
